import type { ReactNode } from "react";
import { Card, StepHeading, StepShell, SuccessMoment } from "@codel/dova-ds";
import { Button } from "../onboarding/components/Button";
import type { ExpectedOutcome } from "../expectedOutcome";

/** The one way out of every terminal flow screen. */
export const BACK_TO_HOME = "Back to home";

/**
 * A terminal flow screen that explains a state rather than asking for anything: a title, a
 * line under it, an optional soft note, then exactly one way out in the footer. Same card
 * shell as onboarding (`StepShell`), so an ending reads as part of the flow it ends.
 * Deliberately no form: offering an action that cannot succeed would be a second dead end.
 */
export function FlowMessageScreen({
  title,
  body,
  note,
  children,
  actionLabel = BACK_TO_HOME,
  onAction,
}: {
  title: string;
  body?: string;
  /** Rendered in a soft card under the heading. */
  note?: ReactNode;
  /** Anything else the state needs to show (e.g. a loading line). */
  children?: ReactNode;
  actionLabel?: string;
  onAction: () => void;
}) {
  return (
    <StepShell footer={<Button onClick={onAction}>{actionLabel}</Button>}>
      <StepHeading title={title}>{body ? <p className="m-0">{body}</p> : null}</StepHeading>
      {note != null ? (
        <Card tone="soft" className="p-4 [font:var(--type-body)] text-secondary">
          {note}
        </Card>
      ) : null}
      {children}
    </StepShell>
  );
}

/**
 * The terminal screen for an EXPECTED outcome (check-in, feedback, reschedule): the request
 * got a business answer, not a crash. It states the outcome and the one move that still
 * works.
 */
export function FlowOutcomeScreen({
  outcome,
  therapistName,
  backLabel = BACK_TO_HOME,
  onBack,
}: {
  outcome: ExpectedOutcome;
  therapistName: string;
  backLabel?: string;
  onBack: () => void;
}) {
  return (
    <FlowMessageScreen
      title={outcome.title}
      body={outcome.body}
      note={outcome.nextStep(therapistName)}
      actionLabel={backLabel}
      onAction={onBack}
    />
  );
}

/** The success screen a flow ends on: a success moment, anything it confirms, then home. */
export function FlowSavedScreen({
  title,
  body,
  children,
  homeLabel = BACK_TO_HOME,
  onHome,
}: {
  title: string;
  body?: string;
  /** Detail under the moment, e.g. the new session a reschedule landed on. */
  children?: ReactNode;
  homeLabel?: string;
  onHome: () => void;
}) {
  return (
    <StepShell footer={<Button onClick={onHome}>{homeLabel}</Button>}>
      <div className="flex flex-1 flex-col justify-center">
        <SuccessMoment title={title} body={body} bodyWidth="container">
          {children}
        </SuccessMoment>
      </div>
    </StepShell>
  );
}
